/**
 * nalli-currency.js â€” display-only currency converter.
 *
 * Replaces the BUCKS Currency Converter app embed
 * (shopify://apps/bucks/blocks/app-embed) with theme-owned code.
 *
 * The full config below was extracted from the LIVE `bucksCC.metaConfig` on
 * shopnalli.myshopify.com (2026-08-01) so behaviour is a like-for-like port:
 *
 *   baseCurrency ............... INR          -> BASE
 *   customSelectors ............ "span.money" -> SELECTOR
 *   selectedCurrencies ......... 151          -> CURRENCIES (148 live, see below)
 *   currencyFormats ............ 172          -> per-currency `f` (money_format)
 *   autoSwitchCurrencyLocationBased  true     -> AUTO entry + geo lookup
 *   showAutoLocationEntry ...... true         -> "AUTO" option is rendered
 *   showCurrencyCodesOnly ...... false        -> BUCKS showed full names
 *   flagDisplayOption .......... showFlagAndCurrency -> superseded, see below
 *
 * UI CHANGED 2026-08-23 (client request): the BUCKS dropdown was rebuilt as the
 * I-was-a-Sari accordion â€” a flag + code summary over the full currency list,
 * every currency shown with its BUCKS display name ("US Dollar (USD)") and a
 * flag from the self-hosted sprite assets/nalli-flags.png. Flag EMOJI were
 * rejected: Windows has no flag glyphs, so they render as letter pairs there.
 * AUTO now resolves the shopper's REAL country (Cloudflare trace) instead of
 * Shopify.country, which is 'IN' for every visitor because nalli.com serves a
 * single India market.
 *   priceRoundingType .......... "none"       -> no rounding applied
 *   showInDesktop/Mobile ....... true         -> no breakpoint gating
 *   cartNotificationStatus ..... false        -> no cart banner
 *
 * THREE of BUCKS's 151 currencies are deliberately dropped because they no
 * longer exist and no rate source publishes them:
 *   BYR - Belarusian ruble, redenominated to BYN in 2016 (BYN is included)
 *   LVL - Latvian lats, replaced by EUR in 2014
 *   STD - Sao Tome dobra, redenominated to STN in 2018
 *
 * IMPORTANT â€” this is DISPLAY-ONLY conversion, exactly like BUCKS was.
 * Checkout still happens in INR. Real presentment currency needs Shopify
 * Markets (shop.enabledPresentmentCurrencies is currently ["INR"]).
 */
(function () {
  'use strict';

  var BASE = 'INR';
  var SELECTOR = 'span.money'; // BUCKS customSelectors â€” every Nalli price uses it
  var STORE_KEY = 'nalli_display_currency';
  var FX_KEY = 'nalli_fx_rates_v1';
  var FX_TTL_MS = 24 * 60 * 60 * 1000;

  /**
   * open.er-api.com â€” free, no API key, 166 currencies, refreshed daily.
   * Chosen over frankfurter.dev (which iwasasari uses) because frankfurter only
   * publishes 30 currencies and cannot cover this 148-currency config.
   */
  var FX_API = 'https://open.er-api.com/v6/latest/' + BASE;

  var CURRENCIES = window.NALLI_CURRENCIES || {};

  /** Flag sprite rows (assets/nalli-flags.png), from nalli-currency-data.js. */
  var FLAG_ROWS = window.NALLI_FLAG_ROWS || {};

  /**
   * Every currency BUCKS offered is listed (147). Three of its entries are gone
   * for good: BYR (redenominated to BYN in 2016), LVL (replaced by EUR in 2014)
   * and STD (redenominated to STN in 2018) â€” no rate source publishes them, so
   * showing them would only ever produce a failed conversion.
   */

  /** One flag from the sprite; blank placeholder keeps rows aligned if unknown. */
  function flagHtml(code) {
    var row = FLAG_ROWS[code];
    if (row === undefined) return '<span class="nalli-cur__flag" aria-hidden="true"></span>';
    return '<span class="nalli-cur__flag" style="--flag-row: ' + row + '" aria-hidden="true"></span>';
  }

  /* ------------------------------------------------------------------ state */

  var rates = null;
  var current = BASE;   // currency actually on screen
  var choice = 'AUTO';  // what the shopper picked ('AUTO' or a code)

  /* ------------------------------------------------------------- formatting */

  /** Decode the HTML entities BUCKS stores in its money_format strings. */
  var decodeCache = {};
  function decode(str) {
    if (decodeCache[str] !== undefined) return decodeCache[str];
    var el = document.createElement('textarea');
    el.innerHTML = str;
    return (decodeCache[str] = el.value);
  }

  function group(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  }

  /**
   * Implements Shopify's money_format placeholders, which is what BUCKS's
   * currencyFormats are written against.
   */
  function formatAmount(value, fmt) {
    var neg = value < 0;
    var abs = Math.abs(value);

    function build(decimals, thousandSep, decimalSep) {
      var fixed = abs.toFixed(decimals);
      var parts = fixed.split('.');
      var out = group(parts[0], thousandSep);
      if (decimals > 0) out += decimalSep + parts[1];
      return out;
    }

    return decode(fmt).replace(
      /\{\{\s*(amount_no_decimals_with_comma_separator|amount_with_comma_separator|amount_no_decimals|amount)\s*\}\}/g,
      function (_m, token) {
        var s;
        if (token === 'amount') s = build(2, ',', '.');
        else if (token === 'amount_no_decimals') s = build(0, ',', '.');
        else if (token === 'amount_with_comma_separator') s = build(2, '.', ',');
        else s = build(0, '.', ',');
        return neg ? '-' + s : s;
      }
    );
  }

  /* ------------------------------------------------------------------ rates */

  function cachedRates() {
    try {
      var raw = localStorage.getItem(FX_KEY);
      if (!raw) return null;
      var d = JSON.parse(raw);
      if (!d || !d.ts || !d.rates) return null;
      if (Date.now() - d.ts > FX_TTL_MS) return null;
      return d.rates;
    } catch (e) { return null; }
  }

  function fetchRates() {
    return fetch(FX_API)
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!j || j.result !== 'success' || !j.rates) throw new Error('bad fx payload');
        rates = j.rates;
        rates[BASE] = 1;
        try { localStorage.setItem(FX_KEY, JSON.stringify({ ts: Date.now(), rates: rates })); } catch (e) {}
        return rates;
      });
  }

  /* -------------------------------------------------------------- conversion */

  /**
   * Cache the original BASE-currency text on the node itself the first time we
   * touch it, so repeated switches always convert from INR and never compound.
   */
  var ORIG = '__nalliBaseText';
  var BASE_ATTR = 'data-nalli-base';

  function parseBase(text) {
    // Nalli renders en-IN: "â‚¹12,283.00". Strip everything but digits/.,
    var cleaned = text.replace(/[^\d.,-]/g, '').replace(/,/g, '');
    var n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  function convertNode(el, code) {
    // The INR text is also kept in an attribute: SearchTap mounts its Vue app with
    // the page's innerHTML as template, which copies already-converted prices into
    // fresh nodes that lack the property; without this they get converted twice.
    if (el[ORIG] === undefined) el[ORIG] = el.getAttribute(BASE_ATTR) || el.textContent;
    if (!el.hasAttribute(BASE_ATTR)) el.setAttribute(BASE_ATTR, el[ORIG]);
    var baseVal = parseBase(el[ORIG]);
    if (baseVal === null) return;

    if (code === BASE) { el.textContent = el[ORIG]; return; }

    var rate = rates && rates[code];
    if (!rate) { el.textContent = el[ORIG]; return; }

    var cfg = CURRENCIES[code];
    // priceRoundingType is "none" in the BUCKS config â€” do not round.
    el.textContent = formatAmount(baseVal * rate, cfg ? cfg.f : '{{amount}} ' + code);
  }

  function applyAll(code) {
    var nodes = document.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) convertNode(nodes[i], code);
    document.documentElement.setAttribute('data-nalli-currency', code);
  }

  /* --------------------------------------------------------------- geo AUTO */

  var COUNTRY_KEY = 'nalli_real_country';
  var COUNTRY_TTL_MS = 24 * 60 * 60 * 1000;

  function cachedCountry() {
    try {
      var d = JSON.parse(localStorage.getItem(COUNTRY_KEY) || 'null');
      if (!d || !d.ts || !d.country) return null;
      if (Date.now() - d.ts > COUNTRY_TTL_MS) return null;
      return d.country;
    } catch (e) { return null; }
  }

  /**
   * The shopper's REAL country. Shopify.country is useless here: nalli.com
   * serves one India market, so it reports 'IN' to every visitor worldwide.
   */
  function detectCurrency() {
    var cached = cachedCountry();
    if (cached) return Promise.resolve(countryToCurrency(cached) || BASE);

    return fetch('https://www.cloudflare.com/cdn-cgi/trace')
      .then(function (r) { return r.text(); })
      .then(function (t) {
        var m = t.match(/loc=([A-Z]{2})/);
        if (!m) return BASE;
        try { localStorage.setItem(COUNTRY_KEY, JSON.stringify({ ts: Date.now(), country: m[1] })); } catch (e) {}
        return countryToCurrency(m[1]) || BASE;
      })
      .catch(function () {
        var cc = (window.Shopify && window.Shopify.country) || '';
        return countryToCurrency(cc) || BASE;
      });
  }

  /** Minimal countryâ†’currency map for the markets Nalli actually sells into. */
  var COUNTRY_CURRENCY = {
    IN: 'INR', US: 'USD', GB: 'GBP', AE: 'AED', SG: 'SGD', AU: 'AUD', CA: 'CAD',
    NZ: 'NZD', MY: 'MYR', LK: 'LKR', NP: 'NPR', ZA: 'ZAR', JP: 'JPY', CN: 'CNY',
    HK: 'HKD', CH: 'CHF', SE: 'SEK', NO: 'NOK', DK: 'DKK', QA: 'QAR', KW: 'KWD',
    BH: 'BHD', OM: 'OMR', SA: 'SAR', TH: 'THB', ID: 'IDR', PH: 'PHP', KR: 'KRW',
    BD: 'BDT', PK: 'PKR', MU: 'MUR', FJ: 'FJD', IL: 'ILS', TR: 'TRY', RU: 'RUB',
    BR: 'BRL', MX: 'MXN', AR: 'ARS', PL: 'PLN', CZ: 'CZK', HU: 'HUF', RO: 'RON'
  };
  var EURO_ZONE = 'AT BE CY EE FI FR DE GR IE IT LV LT LU MT NL PT SK SI ES HR'.split(' ');

  function countryToCurrency(cc) {
    if (COUNTRY_CURRENCY[cc]) return COUNTRY_CURRENCY[cc];
    if (EURO_ZONE.indexOf(cc) !== -1) return 'EUR';
    return null;
  }

  /* ------------------------------------------------------------------- UI */

  /** Every instance on the page: header (desktop) + menu drawer. */
  var hosts = [];

  /** BASE first, then every other currency in the order BUCKS listed them. */
  function optionCodes() {
    var all = Object.keys(CURRENCIES);
    var rest = all.filter(function (c) { return c !== BASE; });
    return (CURRENCIES[BASE] ? [BASE] : []).concat(rest);
  }

  function optionHtml(value, flag, text) {
    return '<li class="nalli-cur__item">' +
           '<button type="button" class="nalli-cur__option" data-nalli-currency-option="' + value + '">' +
           flag + '<span class="nalli-cur__name">' + text + '</span></button></li>';
  }

  function render(host) {
    var list = host.querySelector('[data-nalli-currency-list]');
    if (!list || list.childElementCount) return;
    var html = optionHtml('AUTO', '<span class="nalli-cur__flag nalli-cur__flag--auto" aria-hidden="true"></span>', 'Auto (by location)');
    optionCodes().forEach(function (c) {
      html += optionHtml(c, flagHtml(c), CURRENCIES[c].n);
    });
    list.innerHTML = html;
  }

  /** Summary shows the flag + code of the currency actually on screen. */
  function setLabel(choice, code) {
    var html = code ? flagHtml(code) + '<span class="nalli-cur__code">' + code + '</span>' : '';
    hosts.forEach(function (host) {
      var label = host.querySelector('[data-nalli-currency-label]');
      if (label) label.innerHTML = html;
    });
  }

  function setActive(choice) {
    hosts.forEach(function (host) {
      var opts = host.querySelectorAll('[data-nalli-currency-option]');
      for (var i = 0; i < opts.length; i++) {
        if (opts[i].getAttribute('data-nalli-currency-option') === choice) {
          opts[i].setAttribute('aria-current', 'true');
        } else {
          opts[i].removeAttribute('aria-current');
        }
      }
    });
  }

  function closeAll() {
    hosts.forEach(function (host) {
      var d = host.querySelector('details');
      if (d) d.removeAttribute('open');
    });
  }

  function choose(next, persist) {
    choice = next;
    var resolve = next === 'AUTO' ? detectCurrency() : Promise.resolve(next);
    return resolve.then(function (code) {
      if (!CURRENCIES[code]) code = BASE;
      current = code;
      if (persist) { try { localStorage.setItem(STORE_KEY, next); } catch (e) {} }
      setActive(next);
      setLabel(next, code);
      applyAll(code);
    });
  }

  /** Bind one instance; safe to call twice. */
  function bindHost(host) {
    if (host.__nalliCurBound) return;
    host.__nalliCurBound = true;
    host.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-nalli-currency-option]');
      if (!opt) return;
      e.preventDefault();
      choose(opt.getAttribute('data-nalli-currency-option'), true);
      closeAll();
    });
  }

  /**
   * Keep every instance on the page usable. Two things happen after init on a
   * real page and both are handled here, idempotently:
   *   - a switcher mounts late (the menu drawer markup on product pages), and
   *   - an existing switcher gets its inner markup re-rendered by the header /
   *     drawer components, which empties the option list we had built.
   * Identity checks alone are not enough for the second case, so every host is
   * re-checked; render() and bindHost() are both no-ops when already done.
   */
  function syncHosts() {
    hosts = Array.prototype.slice.call(document.querySelectorAll('nalli-currency-form'));
    var changed = false;
    hosts.forEach(function (host) {
      if (!host.__nalliCurBound) { bindHost(host); changed = true; }
      var list = host.querySelector('[data-nalli-currency-list]');
      if (list && !list.childElementCount) { render(host); changed = true; }
    });
    if (changed) {
      setActive(choice);
      setLabel(choice, current);
    }
  }

  /* ---------------------------------------------------------------- bootstrap */

  function init() {
    if (window.__nalliCurrencyInit) return;
    if (!Object.keys(CURRENCIES).length) return;
    window.__nalliCurrencyInit = true;

    syncHosts();

    try { choice = localStorage.getItem(STORE_KEY) || 'AUTO'; } catch (e) {}
    setActive(choice);
    if (choice !== 'AUTO') setLabel(choice, choice);

    rates = cachedRates();
    var ready = rates ? Promise.resolve(rates) : fetchRates().catch(function () { return null; });
    ready.then(function () { return choose(choice, false); });

    // Click anywhere else closes an open switcher.
    document.addEventListener('click', function (e) {
      hosts.forEach(function (host) {
        var d = host.querySelector('details');
        if (d && d.hasAttribute('open') && !host.contains(e.target)) d.removeAttribute('open');
      });
    });

    /**
     * Two jobs, debounced: adopt switcher instances that mount late (menu
     * drawer), and re-convert `span.money` nodes injected by SearchTap's PLP
     * re-render, the cart drawer, or variant price swaps.
     */
    var pending = null;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(function () {
        syncHosts();
        if (current !== BASE) applyAll(current);
      }, 120);
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
